/* ===================================================================
   useListingsHealth() -- scored NAP / listings-health for the signed-in
   client.

   Binds to GET /api/v1/listings/nap (engine/api/v1/app.py) through the typed
   api client (src/lib/api.ts). The backend compares Name/Address/Phone across
   Google, Yelp, and Facebook and returns a transparent 0-100 score, a band,
   an owner-friendly summary, the per-field consistency, the score breakdown,
   and suggested fixes with edit URLs.

   AUTH: api.ts attaches the Bearer rr_ API key from localStorage. The query
   is disabled until a token exists, so it never fires a guaranteed 401 on
   first paint -- the same gating pattern as the other data hooks.

   HONEST UNKNOWNS: a platform with no credentials (or whose fetch failed) is
   reported as 'unknown' in the payload, NOT a fabricated value or a real 0.
   The Listings screen renders that state distinctly.
   =================================================================== */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, type ListingsHealth } from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export interface UseListingsHealthParams {
  /** Force-disable even if a token exists (e.g. screen not ready). */
  enabled?: boolean;
}

export function useListingsHealth(
  params: UseListingsHealthParams = {},
): UseQueryResult<ListingsHealth, Error> {
  const hasToken = Boolean(useApiToken());

  return useQuery({
    queryKey: qk.listingsHealth(),
    queryFn: ({ signal }) => api.getListingsHealth(signal),
    enabled: hasToken && params.enabled !== false,
    // The daily MonitorAgent scan only changes once a day; keep it fresh-ish
    // without hammering the directories on every navigation.
    staleTime: 5 * 60_000,
  });
}
